import { useCallback } from "react";
import type { User } from "@ferrivia/api-contract";
import { AuthGate, useLocaleSync } from "@ferrivia/auth-web";
import { BrowserRouter, Route, Routes } from "react-router";
import { AppShell } from "./app/AppShell";
import { markBootReady } from "./boot/bootSplash";
import { EmailOtpLoginScreen, LoginScreen } from "./components/LoginScreen";
import { useAppViewport } from "./hooks/useAppViewport";
import { MARKET } from "./lib/market";
import { OnboardingGate } from "./onboarding";
import { purgeClientState } from "./offline/purge";

function AuthedApp({ user, signOut }: { user: User; signOut: () => void }) {
  useLocaleSync(user);
  // Sign-out is the app-initiated purge; the API client's 401 choke point
  // (main.tsx's onUnauthorized) covers "signed out elsewhere".
  const signOutAndPurgeCache = useCallback(() => {
    purgeClientState();
    signOut();
  }, [signOut]);

  return (
    // Outside BrowserRouter on purpose — the gate is a screen, not a
    // route. See OnboardingGate.
    <OnboardingGate>
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<AppShell user={user} signOut={signOutAndPurgeCache} />} />
        </Routes>
      </BrowserRouter>
    </OnboardingGate>
  );
}

const US_PASSKEY_ENROLMENT_AUTH_METHODS = ["eid", "email_otp"] as const;

export function App() {
  useAppViewport();

  return (
    <AuthGate
      renderLogin={({ initialErrorCode, onSignedIn }) =>
        MARKET === "us" ? (
          <EmailOtpLoginScreen initialErrorCode={initialErrorCode} onSignedIn={onSignedIn} />
        ) : (
          <LoginScreen initialErrorCode={initialErrorCode} onSignedIn={onSignedIn} />
        )
      }
      onBootReady={markBootReady}
      allowCachedSession
      passkeyEnrolmentAuthMethods={MARKET === "us" ? US_PASSKEY_ENROLMENT_AUTH_METHODS : undefined}
    >
      {({ user, signOut }) => <AuthedApp user={user} signOut={signOut} />}
    </AuthGate>
  );
}
